import { useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { User } from 'src/models/user';
import GrantProfessionalAccessModal from './GrantProfessionalAccessModal';
import ConfirmModal from 'src/components/shared/ConfirmModal';
import {
  FormattedDate,
  ProfessionalAccessData,
  accessExpiryDate,
  assessmentAccessTypeLabel,
  isFixedAccess,
  parseFormattedDate,
  proFeatureLabel
} from 'src/models/professionalAccess';
import { formatDate } from 'src/utils/dates';

const PROFESSIONAL_ACCESS_REVOKE = gql`
  mutation ProfessionalAccessRevoke($input: ProfessionalAccessRevokeInput!) {
    ProfessionalAccessRevoke(input: $input) {
      id
    }
  }
`;

type Props = {
  user: User;
  refreshUser: (email: string) => void;
};

const formatOptionalDate = (value?: FormattedDate): string => {
  const date = parseFormattedDate(value);
  return date ? formatDate({ date, fullMonth: false }) : '—';
};

const AccessCard = ({
  access,
  onRevoke
}: {
  access: ProfessionalAccessData;
  onRevoke: (access: ProfessionalAccessData) => void;
}) => {
  const fixed = isFixedAccess(access);
  const expiry = accessExpiryDate(access);

  return (
    <Card variant="outlined">
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2
          }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {(access.features ?? []).map((feature) => (
              <Chip
                key={feature}
                label={proFeatureLabel(feature)}
                color="primary"
                size="small"
              />
            ))}
          </Stack>
          <Button
            color="error"
            variant="outlined"
            size="small"
            onClick={() => onRevoke(access)}
          >
            Revoke
          </Button>
        </Box>

        <Stack spacing={0.5}>
          <DetailRow
            label="Assessment access"
            value={assessmentAccessTypeLabel(access.assessmentAccessType)}
          />
          {fixed && (
            <>
              <DetailRow
                label="Assessments (used / total)"
                value={`${access.usedAssessments ?? 0} / ${
                  access.totalAssessments ?? 0
                }`}
              />
              <DetailRow
                label="Available"
                value={`${access.availableAssessments ?? 0}`}
              />
              {!!access.inProgressAssessments && (
                <DetailRow
                  label="In progress"
                  value={`${access.inProgressAssessments}`}
                />
              )}
            </>
          )}
          {!fixed && (
            <DetailRow
              label="Headset limit"
              value={`${access.headsetLimit ?? 0}`}
            />
          )}
          <DetailRow
            label="Client Trial Membership Days"
            value={`${access.grantsLinkedUserMembershipTrialDays ?? 0}`}
          />
          <DetailRow
            label="Assigned"
            value={formatOptionalDate(access.assignedDate ?? access.created)}
          />
          <DetailRow
            label="Expiry"
            value={expiry ? formatDate({ date: expiry, fullMonth: false }) : 'No expiry'}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    <Typography variant="body2" color="textSecondary" sx={{ minWidth: 220 }}>
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

const UserProfessionalAccess = ({ user, refreshUser }: Props) => {
  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<
    ProfessionalAccessData | undefined
  >();

  const [executeRevoke, { loading: revokeLoading }] = useMutation(
    PROFESSIONAL_ACCESS_REVOKE,
    { errorPolicy: 'all' }
  );

  const activeAccess = user.activeProfessionalAccess();
  const expiredAccess = user.expiredProfessionalAccess();

  // An active Pro subscription grants Pro Boost automatically, so surface that
  // separately from the manually granted professional feature access below.
  const hasProSubscription = !!user.proMembership?.isActiveOrTrial();

  const handleConfirmRevoke = async () => {
    if (!revokeTarget) return;
    const result = await executeRevoke({
      variables: { input: { professionalAccessId: revokeTarget.id } }
    });
    if (result.data?.ProfessionalAccessRevoke) {
      // Re-fetch the user so the revoked node drops out of the active list.
      refreshUser(user.email);
    }
    setRevokeTarget(undefined);
  };

  return (
    <Box sx={{ p: 3 }}>
      {hasProSubscription && (
        <>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Subscription
          </Typography>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                <Chip
                  label={proFeatureLabel('PRO_BOOST')}
                  color="primary"
                  size="small"
                />
              </Stack>
              <Typography variant="body2" color="textSecondary">
                This subscription has granted access to Pro Boost.
              </Typography>
            </CardContent>
          </Card>
        </>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}
      >
        <Typography variant="h5">Professional Feature Access</Typography>
        <Button variant="contained" onClick={() => setGrantModalOpen(true)}>
          {activeAccess.length > 0 ? 'Change access' : 'Grant access'}
        </Button>
      </Box>

      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
        Active access
      </Typography>
      {activeAccess.length === 0 ? (
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          This user has no active professional feature access.
        </Typography>
      ) : (
        <Stack spacing={2} sx={{ mb: 3 }}>
          {activeAccess.map((access) => (
            <AccessCard
              key={access.id}
              access={access}
              onRevoke={setRevokeTarget}
            />
          ))}
        </Stack>
      )}

      {expiredAccess.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
            Expired / revoked access
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Features</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Used / Total</TableCell>
                <TableCell>Assigned</TableCell>
                <TableCell>Expiry</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expiredAccess.map((access) => {
                const expiry = accessExpiryDate(access);
                return (
                  <TableRow key={access.id}>
                    <TableCell>
                      {(access.features ?? [])
                        .map((f) => proFeatureLabel(f))
                        .join(', ')}
                    </TableCell>
                    <TableCell>
                      {assessmentAccessTypeLabel(access.assessmentAccessType)}
                    </TableCell>
                    <TableCell>
                      {isFixedAccess(access)
                        ? `${access.usedAssessments ?? 0} / ${
                            access.totalAssessments ?? 0
                          }`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {formatOptionalDate(access.assignedDate ?? access.created)}
                    </TableCell>
                    <TableCell>
                      {expiry
                        ? formatDate({ date: expiry, fullMonth: false })
                        : 'No expiry'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </>
      )}

      {grantModalOpen && (
        <GrantProfessionalAccessModal
          open={grantModalOpen}
          userId={user.id}
          hasActiveAccess={activeAccess.length > 0}
          existingAccess={activeAccess[0]}
          onClose={() => setGrantModalOpen(false)}
          onGranted={() => refreshUser(user.email)}
        />
      )}

      <ConfirmModal
        confirmModalOpen={!!revokeTarget}
        handleConfirm={handleConfirmRevoke}
        closeConfirmModal={() => setRevokeTarget(undefined)}
        handleCloseConfirm={() => setRevokeTarget(undefined)}
        heading="Revoke professional access"
        subheading="Type 'REVOKE' to confirm"
        confirmText="REVOKE"
        bodyText={
          revokeLoading
            ? 'Revoking…'
            : 'This moves the access node to expired. It applies regardless of remaining assessments.'
        }
      />
    </Box>
  );
};

export default UserProfessionalAccess;
